import { Component, type ErrorInfo } from "react";
import { v4 } from "uuid";
import type { ErrorBoundaryProperties } from "./ErrorBoundary.properties.ts";
import type { ErrorBoundaryState } from "./ErrorBoundary.state.ts";

class ErrorBoundary extends Component<
  ErrorBoundaryProperties,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProperties) {
    super(props);

    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  extractCrashedComponentName = (
    componentStackLines: string | null | undefined,
  ): string | null => {
    const parts = componentStackLines?.split("\n");

    if (!parts) return null;

    for (const entry of parts) {
      if (entry && !entry.includes("ErrorBoundary") && entry.includes("@")) {
        const [component] = entry.split("@");
        return component.trim();
      }
    }

    return null;
  };

  componentDidCatch(error: Error, info: ErrorInfo) {
    const uuid = v4();
    const componentName = this.extractCrashedComponentName(info.componentStack);

    if (componentName) {
      this.props.logError(error.message, componentName, uuid);

      if (error.stack) {
        this.props.logTrace(error.stack, componentName, uuid);
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
